import {initialState} from './reducers/initialState';
import {type IState} from './models/IState';
import {rootReducer} from './reducers/rootReducer';

export interface DispatchAction<T> {
	type: string;
	payload: T;
}
interface Store<S> {
	/**
	 * Used to update the state of the application.
	 * @param action A plain object representing the change in state.
	 */
	dispatch<T>(action: DispatchAction<T>): void;

	/**
	 * A readonly object of the current state of the application. This should not be mutated.
	 */
	getState(): Readonly<S>;

	/**
	 * Used to listen for updates to the state. Ex: Rerendering UI when data changes.
	 * @param listener The callback for when the state changes.
	 * @returns A void function to cancel the listeners from further updates.
	 */
	subscribe(listener: () => void): () => void;
}

/**
 * Should be treated as the single source of truth for the modal state.
 */
export const store = createStore<IState>(rootReducer, initialState);

/**
 * Creates a simplified redux store.
 * Simplified version of: https://github.com/reduxjs/redux/blob/master/src/createStore.ts
 */
function createStore<S>(reducer: <T>(state: S, action: DispatchAction<T>) => S, preloadedState: S): Store<S> {
	let isDispatching = false;
	const currentReducer = reducer;
	let currentState: S = preloadedState;

	let currentListeners: Array<() => void> | null = [];
	let nextListeners = currentListeners;

	const dispatch = <T>(action: DispatchAction<T>) => {
		if (typeof action !== 'object') {
			throw new TypeError('You may only dispatch plain objects. Received: ' + typeof action);
		}

		if (typeof action.type === 'undefined') {
			throw new TypeError('You may not have an undefined "type" property.');
		}

		if (isDispatching) {
			throw new Error('Reducers may not dispatch actions.');
		}

		try {
			isDispatching = true;
			currentState = currentReducer(currentState, action);
		} finally {
			isDispatching = false;
		}

		// eslint-disable-next-line no-multi-assign
		const listeners = (currentListeners = nextListeners);
		for (let i = 0; i < listeners?.length; i++) {
			const listener = listeners[i];
			listener?.();
		}

		return action;
	};

	const getState = () => {
		if (isDispatching) {
			throw new Error('You may not call getState from within a reducer.');
		}

		return currentState;
	};

	const subscribe = (listener: () => void) => {
		if (typeof listener !== 'function') {
			throw new TypeError('Expected a listener to be a function. Instead received: ' + typeof listener);
		}

		if (isDispatching) {
			throw new Error('You may not add a subscriber from a subscription function.');
		}

		let isSubscribed = true;
		if (nextListeners === currentListeners) {
			nextListeners = currentListeners?.slice() ?? null;
		}

		nextListeners?.push(listener);

		return () => {
			if (!isSubscribed) {
				return;
			}

			if (isDispatching) {
				throw new Error('You may not remove a subscriber while reducing or inside a subscription function.');
			}

			isSubscribed = false;

			if (nextListeners === currentListeners) {
				nextListeners = currentListeners?.slice() ?? null;
			}

			const index = nextListeners?.indexOf(listener) ?? 0;
			nextListeners.slice(index, 1);
			currentListeners = null;
		};
	};

	dispatch({type: 'init'} as DispatchAction<unknown>);

	const store: Store<S> = {
		dispatch,
		getState,
		subscribe,
	};

	return store;
}
